import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import StateBadge from "@/components/StateBadge";
import api from "@/lib/api";
import {
  Calendar,
  Clock,
  MapPin,
  Sparkles,
  User,
  Building2,
  Users as PeopleIcon,
  Link as LinkIcon,
  Play,
  AlertTriangle,
} from "lucide-react";

/**
 * /preview/campaign/:token — public read-only campaign preview.
 * No auth. Advertiser generates a share link and sends it to a client / partner
 * to show what will run, when, where, and under what programmable rules.
 */

const ADVERTISER_ICONS = {
  artist: User,
  business: Building2,
  individual: PeopleIcon,
};

export default function PreviewCampaign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/ads/campaigns/share/${token}`);
        setData(r.data);
      } catch (e) {
        setError(
          e?.response?.status === 404
            ? "This share link is not valid or has been revoked."
            : "Could not load preview."
        );
      }
    })();
  }, [token]);

  if (error) {
    return (
      <PublicShell>
        <div className="pt-40 pb-32 text-center px-6">
          <AlertTriangle size={32} className="text-red-400 mx-auto mb-4" />
          <div
            data-testid="preview-error"
            className="text-white/60 text-lg max-w-md mx-auto"
          >
            {error}
          </div>
        </div>
      </PublicShell>
    );
  }

  if (!data) {
    return (
      <PublicShell>
        <div className="pt-40 pb-32 text-center text-white/40">
          Loading preview…
        </div>
      </PublicShell>
    );
  }

  const AdvertiserIcon =
    ADVERTISER_ICONS[data.advertiser_type] || Building2;
  const dayparting = `${String(data.start_hour).padStart(2, "0")}:00 – ${String(data.end_hour).padStart(2, "0")}:00`;
  const isVideo = data.creative_type === "video";

  return (
    <PublicShell>
      <section className="pt-28 md:pt-32 border-b border-white/10">
        <div
          data-testid="preview-page"
          className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16"
        >
          <div className="flex items-center gap-3 mb-4">
            <StateBadge state="PREVIEW" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              Campaign preview · read-only
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
            {/* Creative */}
            <div>
              <div className="aspect-[4/5] md:aspect-video bg-black border border-white/10 overflow-hidden relative">
                {isVideo ? (
                  <video
                    data-testid="preview-creative-video"
                    src={data.creative_url}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                  />
                ) : (
                  <img
                    data-testid="preview-creative-image"
                    src={data.creative_url}
                    alt={data.name}
                    className="w-full h-full object-cover"
                  />
                )}
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur px-3 py-1.5 border border-white/10">
                  <Play size={11} className="text-[#E8D6A3]" />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-white/70">
                    On network
                  </span>
                </div>
              </div>
              {data.click_url && (
                <a
                  href={data.click_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="preview-click-url"
                  className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/50 hover:text-[#E8D6A3] transition"
                >
                  <LinkIcon size={12} /> {data.click_url}
                </a>
              )}
            </div>

            {/* Meta */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <AdvertiserIcon size={14} className="text-[#E8D6A3]" />
                <span
                  data-testid="preview-advertiser"
                  className="text-xs uppercase tracking-[0.22em] text-white/60"
                >
                  {data.advertiser_company || "Advertiser"}
                </span>
              </div>
              <h1
                data-testid="preview-name"
                className="font-display font-black tracking-tighter text-4xl md:text-5xl leading-[0.95]"
              >
                {data.name}
              </h1>
              <div className="mt-6 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
                <Tile
                  icon={<Calendar size={13} />}
                  k="Runs"
                  v={`${data.start_date} → ${data.end_date}`}
                />
                <Tile
                  icon={<Clock size={13} />}
                  k="Dayparting"
                  v={dayparting}
                />
                <Tile
                  icon={<Calendar size={13} />}
                  k="Days"
                  v={
                    data.days_of_week.length === 7
                      ? "Every day"
                      : data.days_of_week.join(" · ")
                  }
                  wide
                />
              </div>

              {/* Targeting */}
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 flex items-center gap-2">
                  <MapPin size={12} /> Placement
                </div>
                {data.targets_all_hubs ? (
                  <div
                    data-testid="preview-all-hubs"
                    className="border border-white/10 p-4 bg-white/[0.02]"
                  >
                    <div className="font-bold text-white">All hubs</div>
                    <div className="text-xs text-white/50 mt-1">
                      Delivered across every screen on the Mirrorloops network.
                    </div>
                  </div>
                ) : (
                  <div
                    data-testid="preview-hubs"
                    className="flex flex-wrap gap-2"
                  >
                    {data.targeted_hubs.map((h) => (
                      <div
                        key={h.id}
                        className="px-3 py-1.5 border border-white/10 bg-white/[0.03] text-xs"
                      >
                        <span className="text-white font-medium">
                          {h.name}
                        </span>{" "}
                        <span className="text-white/40">
                          · {h.city} · {h.format}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rules */}
              {data.rules_count > 0 && (
                <div className="mt-8">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 flex items-center gap-2">
                    <Sparkles size={12} className="text-[#E8D6A3]" />
                    Programmable rules ({data.rules_count})
                  </div>
                  <div
                    data-testid="preview-rules"
                    className="border border-white/10 divide-y divide-white/10"
                  >
                    {data.rules.map((r, i) => (
                      <RuleRow key={i} rule={r} index={i} />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-10 text-[11px] text-white/40 leading-relaxed">
                Shared by the advertiser · read-only preview. This is not a
                paid placement. To actually run a campaign like this,{" "}
                <a href="/" className="text-[#E8D6A3] hover:underline">
                  visit Mirrorloops
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function Tile({ icon, k, v, wide }) {
  return (
    <div className={`p-5 bg-[#0A0908] ${wide ? "col-span-2" : ""}`}>
      <div className="text-[#E8D6A3] mb-2">{icon}</div>
      <div className="text-sm font-bold text-white truncate">{v}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">
        {k}
      </div>
    </div>
  );
}

function RuleRow({ rule, index }) {
  const label =
    rule?.name ||
    `${rule?.when_type || "condition"} → ${rule?.then_type || "action"}`;
  return (
    <div className="p-3 flex items-center gap-3">
      <span className="w-6 h-6 flex items-center justify-center border border-white/10 text-[10px] font-bold text-[#E8D6A3]">
        {index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-medium truncate">{label}</div>
        {rule?.description && (
          <div className="text-[11px] text-white/40 truncate">
            {rule.description}
          </div>
        )}
      </div>
    </div>
  );
}
